import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UseFormSetError } from 'react-hook-form';
import { DealerForm } from '../components/DealerForm';
import { useCreateDealerMutation } from '../hooks/useCreateDealerMutation';
import { handleUserMutationError } from '@/features/users/services/userErrorHandler';
import type { DealerFormInput } from '../schemas/dealer.schema';
import { Alert, TextLink } from '@/shared/components';
import { usePermissions } from '@/features/auth';

export function DealerCreate() {
  const navigate = useNavigate();
  const { hasAllPermissions } = usePermissions();
  const canCreateSecurityGroup = hasAllPermissions([
    { resource: 'security_group', action: 'add' },
    { resource: 'security_group', action: 'manage_dealers' },
  ]);
  const createMutation = useCreateDealerMutation();
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const setErrorRef = useRef<UseFormSetError<DealerFormInput> | null>(null);

  const handleCreate = async (payload: DealerFormInput) => {
    setTopLevelError(null);
    try {
      // mutateAsync so we keep the existing 422 field-error handling below;
      // the hook invalidates the dealer list cache on success.
      const dealer = await createMutation.mutateAsync(payload);
      navigate(`/admin/dealers/${dealer.id}`);
    } catch (err: unknown) {
      const result = handleUserMutationError(err);
      if (result.status === 422 && result.fieldErrors && setErrorRef.current) {
        for (const [field, messages] of Object.entries(result.fieldErrors)) {
          setErrorRef.current(
            field as Parameters<UseFormSetError<DealerFormInput>>[0],
            { type: 'server', message: messages[0] ?? 'Invalid' },
          );
        }
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to create dealer';
      setTopLevelError(message);
    }
  };

  return (
    <section className="space-y-4">

      <div className="flex items-center gap-2">
        <TextLink to="/admin/dealers" color="secondary" className="text-sm">
          &larr; Back to Dealers
        </TextLink>
      </div>
      <h1 className="text-2xl font-semibold">Create Dealer</h1>
      {topLevelError && (
        <Alert variant="error">
          <span>{topLevelError}</span>
        </Alert>
      )}
      <DealerForm
        mode="create"
        canCreateSecurityGroup={canCreateSecurityGroup}
        onSubmit={handleCreate}
        onSetErrorRef={(setError) => { setErrorRef.current = setError as UseFormSetError<DealerFormInput>; }}
        submitLabel="Create dealer"
        isLoading={createMutation.isPending}
      />
    </section>
  );
}
